import {
  type SignInResponse,
  signIn,
  getSession,
  signOut
} from 'next-auth/react';
import { useRouter } from 'next/router';
import PasswordReset from '../../components/auth/PasswordReset';
import PasswordSetChallenge from '../../components/auth/PasswordSetChallenge';
import { useForm } from 'react-hook-form';
import { DefaultPasswordInput } from '../../components/common/elements/PasswordInput';
import EmailInput from '../../components/common/elements/EmailInput';
import {
  Container,
  ContentBox,
  ErrorText
} from '../../styles/chime-react/CommonStyles';
import styled from 'styled-components';
import PrimaryButton from '../../components/chat/Button/PrimaryButton';
import Heading from '../../components/chat/Heading';
import { useEffect, useState } from 'react';
import { useLoading } from '../../providers/LoadingProvider';
import { ErrorMessageConstant } from '../../constants';
import { useDebouncedCallback } from 'use-debounce';

type SigninForm = { email: string; password: string };

export const BottomButtonWrap = styled.div`
  position: relative;
  text-align: center;

  &:not(:first-child) {
    padding-top: 2.5rem;
  }

  & {
    .title-text {
      font-size: ${(props) => props.theme.fontSizes.text.fontSize};
      color: ${(props) => props.theme.colors.greys.grey50};
      margin-bottom: 0.5rem;
    }

    .error-text {
      white-space: pre-wrap;
      text-align: left;
      display: flex;
      justify-content: center;
      margin-block: -1.75rem 0.8125rem;
    }

    .forgot-password {
      margin-top: 1.5rem;
      font-size: ${(props) => props.theme.fontSizes.small.fontSize};
      color: ${(props) => props.theme.colors.greys.grey50};
      text-align: center;
    }

    .forgot-link {
      margin-left: 0.5rem;
      color: ${(props) => props.theme.colors.primary.main};
    }

    .large-button {
      max-width: 17rem;
      width: 100%;
    }
  }
`;

const SigninPage = ({ redirectCallback }: { redirectCallback?: any }) => {
  const form = useForm<SigninForm>({
    defaultValues: {
      email: '',
      password: ''
    },
    mode: 'onBlur'
  });
  const {
    handleSubmit,
    formState: { errors, isValidating }
  } = form;
  const { setLoading } = useLoading();
  const [confirmState, setConfirmState] = useState<
    | {
        form: SigninForm;
        session: string;
        name: string;
        action: 'change';
      }
    | { action: 'reset' }
  >();
  const { push } = useRouter();
  useEffect(() => {
    isValidating && form.clearErrors('root');
  }, [isValidating]);
  const login = (inputForm: SigninForm, newPassword = '', session = '') => {
    setLoading(true);
    signIn('password', {
      mail: inputForm.email,
      password: inputForm.password,
      newPassword,
      session,
      redirect: false
    }).then((result) => loginRedirect(inputForm, result));
  };
  const loginRedirect = (inputForm: SigninForm, result?: SignInResponse) => {
    if (result?.ok) {
      getSession().then((session) => {
        setLoading(false);
        session && 'newUser' in session
          ? ((newUser) =>
              setConfirmState({
                action: 'change',
                form: inputForm,
                name: newUser.name,
                session: newUser.session
              }))(
              session.newUser as {
                mail: string;
                name: string;
                session: string;
              }
            )
          : redirectCallback
          ? redirectCallback(false, session)
          : push('/dashboard');
      });
    } else if (result?.error) {
      let errorMessage = '';
      const errorCode = result.error as keyof typeof ErrorMessageConstant;
      switch (errorCode) {
        case 'ER008':
          errorMessage =
            'ログインの試行回数が制限を超えました。15分後に再度お試しください。';
          break;

        default:
          errorMessage =
            ErrorMessageConstant[errorCode] ||
            'メールアドレスまたはパスワードが正しくありません。';
          break;
      }
      setLoading(false);
      form.setError('root', {
        message: errorMessage
      });
    } else {
      setLoading(false);
      form.setError('root', {
        message: 'メールアドレスまたはパスワードが正しくありません。'
      });
    }
  };
  const getConfirmElement = (state: typeof confirmState) => {
    switch (state?.action) {
      case 'change':
        return (
          <PasswordSetChallenge
            name={state.name}
            signIn={(newPassword) =>
              login(state.form, newPassword, state.session)
            }
            errorMessage={errors.root?.message}
          />
        );
      case 'reset':
        return <PasswordReset />;
    }
  };

  const loginDebounced = useDebouncedCallback(
    (inputForm) => {
      signOut({ redirect: false }).finally(() => login(inputForm));
    },
    3_000,
    {
      leading: true,
      trailing: false
    }
  );

  return (
    <>
      {confirmState ? (
        getConfirmElement(confirmState)
      ) : (
        <Container maxWidth="582px">
          <Heading level={4} tag="h1" alignCenter={true} mb="2.5rem">
            ログイン
          </Heading>
          <form
            onSubmit={handleSubmit((inputForm) => {
              form.clearErrors('root');
              loginDebounced(inputForm);
            })}
          >
            <ContentBox>
              <EmailInput form={form} required={true} />
              <DefaultPasswordInput form={form} />
            </ContentBox>
            <BottomButtonWrap>
              {!!errors.root?.message && (
                <ErrorText className="error-text">
                  {errors.root.message}
                </ErrorText>
              )}
              <PrimaryButton label="ログイン" />
              <p className="forgot-password">
                ※パスワードを忘れた場合
                <a
                  className="forgot-link"
                  href=""
                  onClick={(e) => {
                    e.preventDefault();
                    setConfirmState({ action: 'reset' });
                  }}
                >
                  再設定メールを送る
                </a>
              </p>
            </BottomButtonWrap>
          </form>
        </Container>
      )}
    </>
  );
};

SigninPage.public = true;

export default SigninPage;
